// src/KeyList.jsx
import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebaseConfig";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
} from "@mui/material";

export default function KeyList() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const fetchKeys = async () => {
      try {
        const snapshot = await getDocs(collection(db, "hymn_keys"));
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setKeys(data);
      } catch (error) {
        console.error("Error fetching hymn keys:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchKeys();
  }, []);

  // Filter by search (use label)
  const filtered = keys.filter((k) =>
    k.label?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort by label
  const sorted = [...filtered].sort((a, b) => {
    if (!a.label || !b.label) return 0;
    return sortOrder === "asc"
      ? a.label.localeCompare(b.label)
      : b.label.localeCompare(a.label);
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, background: "#fafafa" }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Hymn Keys ({sorted.length})
      </Typography>

      {/* Search & Sort */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search keys..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />

        <TextField
          select
          label="Sort"
          size="small"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          sx={{ width: 120 }}
        >
          <MenuItem value="asc">A → Z</MenuItem>
          <MenuItem value="desc">Z → A</MenuItem>
        </TextField>
      </Box>

      {/* Hymn Keys List */}
      {sorted.length > 0 ? (
        sorted.map((k) => (
          <Card
            key={k.id}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: 1,
              transition: "0.25s",
              "&:hover": { boxShadow: 6, transform: "scale(1.01)" },
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                🎵 {k.label}
              </Typography>

              {k.value && (
                <Typography variant="body2" color="text.secondary">
                  Value: {k.value}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          No hymn keys found.
        </Typography>
      )}
    </Paper>
  );
}
